/**
 * useContests — 赛事相关 hook(M3)
 *
 *   • useContests         公开赛事列表
 *   • useContestDetail    单赛事 + 已审核 entries
 *   • 写操作直接调 contestsApi(投稿 / 投票 / 打分),由调用方触发 refresh
 */
import { useCallback, useState } from 'react'

import {
  ApiError,
  contestsApi,
  type SilkContest,
  type SilkContestEntry,
  type SilkContestRewardGrant,
  type SilkContestSeason,
} from '@/api'
import { useHasLoadedRef, useRefetchWhenLocaleChanges } from '@/shared/hooks/useLocaleRefetch'

interface ContestsState {
  items: SilkContest[]
  total: number
  loading: boolean
  error: string | null
}

const INIT_LIST: ContestsState = { items: [], total: 0, loading: false, error: null }

export function useContests(params?: {
  limit?: number
  offset?: number
  enabled?: boolean
  categoryCode?: string
  status?: string
}) {
  const hasLoadedRef = useHasLoadedRef()
  const [state, setState] = useState<ContestsState>(INIT_LIST)
  const limit = params?.limit ?? 20
  const offset = params?.offset ?? 0
  const enabled = params?.enabled !== false
  const categoryCode = params?.categoryCode
  const status = params?.status

  const refresh = useCallback(
    async (silent = false) => {
      if (!enabled) {
        setState(INIT_LIST)
        return
      }
      if (!silent) {
        setState((s) => ({ ...s, loading: true, error: null }))
      }
      try {
        const list = await contestsApi.list({
          limit,
          offset,
          category_code: categoryCode || undefined,
          status: status || undefined,
        })
        setState({ items: list.items, total: list.total, loading: false, error: null })
        hasLoadedRef.current = true
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : (err as Error).message
        setState((s) => ({ ...s, loading: false, error: msg }))
      }
    },
    [enabled, limit, offset, categoryCode, status, hasLoadedRef],
  )

  useRefetchWhenLocaleChanges(refresh, hasLoadedRef)

  return { ...state, refresh: () => refresh(false) }
}

interface DetailState {
  contest: SilkContest | null
  entries: SilkContestEntry[]
  total: number
  loading: boolean
  notFound: boolean
  error: string | null
}

const INIT_DETAIL: DetailState = {
  contest: null,
  entries: [],
  total: 0,
  loading: false,
  notFound: false,
  error: null,
}

export function useContestDetail(id: number | null) {
  const hasLoadedRef = useHasLoadedRef()
  const [state, setState] = useState<DetailState>(INIT_DETAIL)

  const refresh = useCallback(
    async (silent = false) => {
      if (id == null || !Number.isFinite(id)) {
        setState({ ...INIT_DETAIL })
        return
      }
      if (!silent) {
        setState((s) => ({ ...s, loading: true, error: null, notFound: false }))
      }
      try {
        const [contest, entries] = await Promise.all([
          contestsApi.get(id),
          contestsApi.listEntries(id, { limit: 50 }),
        ])
        setState({
          contest,
          entries: entries.items,
          total: entries.total,
          loading: false,
          notFound: false,
          error: null,
        })
        hasLoadedRef.current = true
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setState({ ...INIT_DETAIL, notFound: true })
          return
        }
        const msg = err instanceof ApiError ? err.message : (err as Error).message
        setState((s) => ({ ...s, loading: false, error: msg }))
      }
    },
    [id, hasLoadedRef],
  )

  useRefetchWhenLocaleChanges(refresh, hasLoadedRef)

  return { ...state, refresh: () => refresh(false) }
}

/* ----------------------------- 赛季 ----------------------------- */

interface SeasonsState {
  items: SilkContestSeason[]
  total: number
  loading: boolean
  error: string | null
}
const INIT_SEASONS: SeasonsState = { items: [], total: 0, loading: false, error: null }

export function useContestSeasons(contestId: number | null) {
  const hasLoadedRef = useHasLoadedRef()
  const [state, setState] = useState<SeasonsState>(INIT_SEASONS)
  const refresh = useCallback(
    async (silent = false) => {
      if (contestId == null || !Number.isFinite(contestId)) {
        setState({ ...INIT_SEASONS })
        return
      }
      if (!silent) {
        setState((s) => ({ ...s, loading: true, error: null }))
      }
      try {
        const list = await contestsApi.listSeasons(contestId, { limit: 30 })
        setState({ items: list.items, total: list.total, loading: false, error: null })
        hasLoadedRef.current = true
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : (err as Error).message
        setState((s) => ({ ...s, loading: false, error: msg }))
      }
    },
    [contestId, hasLoadedRef],
  )
  useRefetchWhenLocaleChanges(refresh, hasLoadedRef)
  return { ...state, refresh: () => refresh(false) }
}

interface SeasonDetailState {
  season: SilkContestSeason | null
  grants: SilkContestRewardGrant[]
  loading: boolean
  notFound: boolean
  error: string | null
}
const INIT_SEASON_DETAIL: SeasonDetailState = {
  season: null,
  grants: [],
  loading: false,
  notFound: false,
  error: null,
}

export function useContestSeasonDetail(seasonId: number | null) {
  const hasLoadedRef = useHasLoadedRef()
  const [state, setState] = useState<SeasonDetailState>(INIT_SEASON_DETAIL)
  const refresh = useCallback(
    async (silent = false) => {
      if (seasonId == null || !Number.isFinite(seasonId)) {
        setState({ ...INIT_SEASON_DETAIL })
        return
      }
      if (!silent) {
        setState((s) => ({ ...s, loading: true, error: null, notFound: false }))
      }
      try {
        const [season, grants] = await Promise.all([
          contestsApi.getSeason(seasonId),
          contestsApi.listGrants(seasonId),
        ])
        setState({
          season,
          grants: grants.items,
          loading: false,
          notFound: false,
          error: null,
        })
        hasLoadedRef.current = true
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setState({ ...INIT_SEASON_DETAIL, notFound: true })
          return
        }
        const msg = err instanceof ApiError ? err.message : (err as Error).message
        setState((s) => ({ ...s, loading: false, error: msg }))
      }
    },
    [seasonId, hasLoadedRef],
  )
  useRefetchWhenLocaleChanges(refresh, hasLoadedRef)
  return { ...state, refresh: () => refresh(false) }
}
